import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useErrorReport } from '../../../contexts/ErrorReportContext';
import {
  addCalendarDays,
  calendarDayKey,
  EVENT_LOAD_DAY_WINDOW,
  getEventCalendarDay,
  startOfLocalDay,
} from '../../../services/dateUtils';
import { getEventFeedAdapter } from '../services/eventFeedAdapters';
import { subscribePublicEventPick } from '../../../services/events/publicEventSaveTheDate';
import {
  commitEventFeedCache,
  peekEventFeedCache,
  planFeedRefresh,
} from '../services/eventFeedCache';
import {
  appendUniqueById,
  collectCompletedDayKeys,
  countLoadedByDay,
  featuredDaysNeedingRepair,
  hasMoreFromAggregate,
  isDayComplete,
  incompleteDayKeys,
  nextRange,
  parseCalendarDayKey,
  prependUniqueById,
  replaceEventsInWindow,
  replaceEventsOnDay,
} from '../services/eventFeedLoader';

const FEATURED_EVENTS_PAGE_SIZE = 40;
const SENTINEL_ROOT_MARGIN = '150% 0px';

const EMPTY_AGGREGATE = {
  upcomingCount: 0,
  byDay: {},
  byCategory: {},
  tags: [],
};

const AGGREGATE_CACHE_KEY = 'm2b-event-aggregate-upcomingPublished';

function normalizeAggregate(value) {
  if (!value || typeof value !== 'object') return null;
  if (typeof value.upcomingCount !== 'number') return null;
  return {
    upcomingCount: value.upcomingCount,
    byDay: value.byDay && typeof value.byDay === 'object' ? value.byDay : {},
    byCategory: value.byCategory && typeof value.byCategory === 'object' ? value.byCategory : {},
    tags: Array.isArray(value.tags) ? value.tags : [],
    updatedAt: typeof value.updatedAt === 'number' && Number.isFinite(value.updatedAt)
      ? value.updatedAt
      : null,
  };
}

function readCachedAggregate() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    return normalizeAggregate(JSON.parse(sessionStorage.getItem(AGGREGATE_CACHE_KEY)));
  } catch {
    return null;
  }
}

function writeCachedAggregate(aggregate) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(AGGREGATE_CACHE_KEY, JSON.stringify(aggregate));
  } catch {
    /* quota / private mode */
  }
}

function unionSets(left, right) {
  const next = new Set(left);
  right.forEach((key) => next.add(key));
  return next;
}

/**
 * Homepage upcoming-event feed: day-complete 5-day windows, scroll sentinel, calendar jump.
 *
 * @param {{ selectedDate?: Date|null, selectedDates?: Date[]|null, initialWindowDays?: number }} [opts]
 */
function selectedDayList(selectedDate, selectedDates) {
  const list = Array.isArray(selectedDates)
    ? selectedDates
    : (selectedDate ? [selectedDate] : []);
  return list.filter((day) => day instanceof Date && !Number.isNaN(day.getTime()));
}

export function usePublicEventFeed({
  selectedDate = null,
  selectedDates = null,
  initialWindowDays = EVENT_LOAD_DAY_WINDOW,
} = {}) {
  const { reportError } = useErrorReport();
  const adapter = useMemo(() => getEventFeedAdapter(), []);
  const cachedAggregate = useMemo(() => readCachedAggregate(), []);
  const initialFeed = useMemo(
    () => peekEventFeedCache(calendarDayKey(startOfLocalDay())),
    []
  );
  const initialAggregate = initialFeed?.aggregate || cachedAggregate || EMPTY_AGGREGATE;

  const [events, setEvents] = useState(() => initialFeed?.events || []);
  const [aggregate, setAggregate] = useState(initialAggregate);
  const [countsReady, setCountsReady] = useState(Boolean(initialFeed?.aggregate || cachedAggregate));
  const [loading, setLoading] = useState(() => !(initialFeed?.events?.length));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(() => {
    const through = parseCalendarDayKey(initialFeed?.loadedThrough);
    if (!through || !initialFeed?.aggregate) return false;
    return hasMoreFromAggregate(initialFeed.aggregate.byDay, through);
  });
  const [completedDays, setCompletedDays] = useState(
    () => new Set(initialFeed?.completedDayKeys || [])
  );

  const loadedThroughRef = useRef(parseCalendarDayKey(initialFeed?.loadedThrough) || null);
  const loadingMoreRef = useRef(false);
  const cancelledRef = useRef(false);
  const eventsRef = useRef(initialFeed?.events || []);
  const aggregateRef = useRef(initialAggregate);
  const completedDaysRef = useRef(new Set(initialFeed?.completedDayKeys || []));
  const repairedDaysRef = useRef(new Set());
  const featuredRef = useRef(initialFeed?.featured || []);
  const feedErrorReportedRef = useRef(false);
  const persistFeedRef = useRef(() => {});
  const skipFirstIntersectRef = useRef(true);
  const intersectingRef = useRef(false);
  const sentinelNodeRef = useRef(null);
  const observerRef = useRef(null);
  const reportErrorRef = useRef(reportError);
  const adapterRef = useRef(adapter);

  reportErrorRef.current = reportError;
  adapterRef.current = adapter;

  const syncEvents = useCallback((next) => {
    eventsRef.current = next;
    setEvents(next);
  }, []);

  useEffect(() => subscribePublicEventPick(({ eventId, editorialPick }) => {
    const patch = (list) => list.map((event) => (
      event.id === eventId ? { ...event, editorialPick } : event
    ));
    if (featuredRef.current.some((event) => event.id === eventId)) {
      featuredRef.current = patch(featuredRef.current);
    }
    if (eventsRef.current.some((event) => event.id === eventId)) {
      syncEvents(patch(eventsRef.current));
    }
    persistFeedRef.current();
  }), [syncEvents]);

  const syncCompleted = useCallback((next) => {
    completedDaysRef.current = next;
    setCompletedDays(next);
  }, []);

  const applyAggregate = useCallback((nextAggregate) => {
    const normalized = normalizeAggregate(nextAggregate) || EMPTY_AGGREGATE;
    aggregateRef.current = normalized;
    setAggregate(normalized);
    setCountsReady(true);
    const through = loadedThroughRef.current;
    if (through) {
      setHasMore(hasMoreFromAggregate(normalized.byDay, through));
    }
    if (normalized !== EMPTY_AGGREGATE) {
      writeCachedAggregate(normalized);
    }
    return normalized;
  }, []);

  const persistFeed = useCallback(() => {
    const through = loadedThroughRef.current;
    if (!through) return;
    if (!eventsRef.current.length && !featuredRef.current.length) return;
    commitEventFeedCache({
      at: Date.now(),
      dayKey: calendarDayKey(startOfLocalDay()),
      aggregate: aggregateRef.current,
      featured: featuredRef.current,
      events: eventsRef.current,
      completedDayKeys: [...completedDaysRef.current],
      loadedThrough: calendarDayKey(through),
    });
  }, []);

  persistFeedRef.current = persistFeed;

  const reportFeedLoadError = useCallback((err) => {
    const painted = eventsRef.current.length > 0;
    const silent = painted || feedErrorReportedRef.current;
    if (!silent) feedErrorReportedRef.current = true;
    reportErrorRef.current(err, {
      pageLabel: 'Events',
      source: 'event-feed-load',
      userMessage: 'Could not load events.',
      silent,
    });
  }, []);

  const reportIncompleteDay = useCallback((dayKey, expected, loaded) => {
    reportErrorRef.current('Homepage day did not load every calendar event', {
      pageLabel: 'Events',
      source: 'event-feed-completeness',
      kind: 'intent',
      silent: true,
      detail: JSON.stringify({ day: dayKey, expected, loaded }),
    });
  }, []);

  const completeLoadedRange = useCallback(async (from, to) => {
    const byDay = aggregateRef.current.byDay || {};
    let merged = eventsRef.current;
    const loadedByDay = countLoadedByDay(merged);
    const incomplete = incompleteDayKeys(loadedByDay, byDay, from, to);

    for (const dayKey of incomplete) {
      if (repairedDaysRef.current.has(dayKey)) continue;
      repairedDaysRef.current.add(dayKey);
      const day = parseCalendarDayKey(dayKey);
      if (!day) continue;
      try {
        const extra = await adapterRef.current.getRange({
          start: day,
          end: addCalendarDays(day, 1),
        });
        if (cancelledRef.current) return merged;
        merged = appendUniqueById(merged, extra);
      } catch (err) {
        reportFeedLoadError(err);
      }
    }

    const afterCounts = countLoadedByDay(merged);
    incomplete.forEach((dayKey) => {
      const expected = byDay[dayKey] || 0;
      const loaded = afterCounts[dayKey] || 0;
      if (loaded < expected) {
        reportIncompleteDay(dayKey, expected, loaded);
      }
    });

    if (merged !== eventsRef.current) {
      syncEvents(merged);
    }

    const nextCompleted = unionSets(
      completedDaysRef.current,
      collectCompletedDayKeys(merged, byDay, from, to, repairedDaysRef.current)
    );
    syncCompleted(nextCompleted);
    return merged;
  }, [reportFeedLoadError, reportIncompleteDay, syncCompleted, syncEvents]);

  const repairFeaturedDays = useCallback(async () => {
    const byDay = aggregateRef.current.byDay || {};
    const days = featuredDaysNeedingRepair(
      featuredRef.current,
      countLoadedByDay(eventsRef.current),
      byDay
    );
    if (days.length === 0) return;

    let merged = eventsRef.current;
    for (const day of days) {
      const key = calendarDayKey(day);
      if (repairedDaysRef.current.has(key) && completedDaysRef.current.has(key)) continue;
      repairedDaysRef.current.add(key);
      try {
        const extra = await adapterRef.current.getRange({
          start: day,
          end: addCalendarDays(day, 1),
        });
        if (cancelledRef.current) return;
        merged = appendUniqueById(merged, extra);
      } catch (err) {
        reportFeedLoadError(err);
      }
    }

    const afterCounts = countLoadedByDay(merged);
    days.forEach((day) => {
      const dayKey = calendarDayKey(day);
      const expected = byDay[dayKey] || 0;
      const loaded = afterCounts[dayKey] || 0;
      if (loaded < expected) {
        reportIncompleteDay(dayKey, expected, loaded);
      }
    });

    if (merged !== eventsRef.current) {
      syncEvents(merged);
    }
    const nextCompleted = unionSets(
      completedDaysRef.current,
      collectCompletedDayKeys(
        merged,
        byDay,
        days[0],
        addCalendarDays(days[days.length - 1], 1),
        repairedDaysRef.current
      )
    );
    days.forEach((day) => {
      const key = calendarDayKey(day);
      if (isDayComplete(key, afterCounts, byDay) || repairedDaysRef.current.has(key)) {
        nextCompleted.add(key);
      }
    });
    syncCompleted(nextCompleted);
  }, [reportFeedLoadError, reportIncompleteDay, syncCompleted, syncEvents]);

  const extendLoadedThrough = useCallback(async (needEnd, { followUp = true } = {}) => {
    const through = loadedThroughRef.current;
    if (!through || !needEnd || needEnd <= through) return;
    if (cancelledRef.current || loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const nextEvents = await adapterRef.current.getRange({
        start: through,
        end: needEnd,
      });
      if (cancelledRef.current) return;
      syncEvents(appendUniqueById(eventsRef.current, nextEvents));
      const from = through;
      loadedThroughRef.current = needEnd;
      setHasMore(hasMoreFromAggregate(aggregateRef.current.byDay, needEnd));
      await completeLoadedRange(from, needEnd);
      await repairFeaturedDays();
      persistFeed();
    } catch (err) {
      reportFeedLoadError(err);
    } finally {
      loadingMoreRef.current = false;
      if (!cancelledRef.current) {
        setLoadingMore(false);
      }
    }

    if (
      followUp &&
      !cancelledRef.current &&
      intersectingRef.current &&
      hasMoreFromAggregate(aggregateRef.current.byDay, loadedThroughRef.current)
    ) {
      const next = nextRange(loadedThroughRef.current, EVENT_LOAD_DAY_WINDOW);
      await extendLoadedThrough(next.end, { followUp: false });
    }
  }, [completeLoadedRange, persistFeed, repairFeaturedDays, reportFeedLoadError, syncEvents]);

  const loadNextWindow = useCallback(async () => {
    const through = loadedThroughRef.current;
    if (!through) return;
    if (!hasMoreFromAggregate(aggregateRef.current.byDay, through)) {
      setHasMore(false);
      return;
    }
    await extendLoadedThrough(nextRange(through, EVENT_LOAD_DAY_WINDOW).end);
  }, [extendLoadedThrough]);

  const ensureDayLoaded = useCallback(async (date) => {
    if (!date) return;
    const selected = startOfLocalDay(date);
    const needEnd = addCalendarDays(selected, EVENT_LOAD_DAY_WINDOW);
    await extendLoadedThrough(needEnd);
  }, [extendLoadedThrough]);

  const revalidateFromAggregate = useCallback(async (snapshot, nextAggregate, today) => {
    const plan = planFeedRefresh(snapshot, nextAggregate);
    applyAggregate(nextAggregate);
    if (cancelledRef.current || plan.unchanged) return;

    const through = loadedThroughRef.current || addCalendarDays(today, EVENT_LOAD_DAY_WINDOW);

    if (plan.refetchWindow) {
      const [incoming, featured] = await Promise.all([
        adapterRef.current.getRange({ start: today, end: through }),
        adapterRef.current.getFeatured({ pageSize: FEATURED_EVENTS_PAGE_SIZE }),
      ]);
      if (cancelledRef.current) return;
      featuredRef.current = Array.isArray(featured) ? featured : [];
      syncEvents(prependUniqueById(
        featuredRef.current,
        replaceEventsInWindow(eventsRef.current, today, through, incoming)
      ));
      await completeLoadedRange(today, through);
      await repairFeaturedDays();
      persistFeed();
      return;
    }

    let merged = eventsRef.current;
    for (const dayKey of plan.dayKeys) {
      const day = parseCalendarDayKey(dayKey);
      if (!day) continue;
      const incoming = await adapterRef.current.getRange({
        start: day,
        end: addCalendarDays(day, 1),
      });
      if (cancelledRef.current) return;
      merged = replaceEventsOnDay(merged, dayKey, incoming);
      featuredRef.current = featuredRef.current
        .filter((event) => {
          const eventDay = getEventCalendarDay(event);
          if (!eventDay || calendarDayKey(eventDay) !== dayKey) return true;
          return incoming.some((item) => item?.id === event.id);
        })
        .map((event) => incoming.find((item) => item?.id === event.id) || event);
    }

    if (plan.refetchFeatured) {
      const featured = await adapterRef.current.getFeatured({ pageSize: FEATURED_EVENTS_PAGE_SIZE });
      if (cancelledRef.current) return;
      featuredRef.current = Array.isArray(featured) ? featured : [];
    }

    syncEvents(prependUniqueById(featuredRef.current, merged));
    if (plan.dayKeys.length) {
      const first = parseCalendarDayKey(plan.dayKeys[0]);
      const last = parseCalendarDayKey(plan.dayKeys[plan.dayKeys.length - 1]);
      if (first && last) {
        await completeLoadedRange(first, addCalendarDays(last, 1));
      }
    }
    if (plan.refetchFeatured) await repairFeaturedDays();
    persistFeed();
  }, [applyAggregate, completeLoadedRange, persistFeed, repairFeaturedDays, syncEvents]);

  useEffect(() => {
    cancelledRef.current = false;
    skipFirstIntersectRef.current = true;
    intersectingRef.current = false;
    repairedDaysRef.current = new Set();
    feedErrorReportedRef.current = false;

    const today = startOfLocalDay();
    const windowDays = initialWindowDays > 0 ? initialWindowDays : EVENT_LOAD_DAY_WINDOW;
    const firstEnd = addCalendarDays(today, windowDays);
    const snapshot = peekEventFeedCache(calendarDayKey(today));
    const snapshotThrough = snapshot?.loadedThrough
      ? parseCalendarDayKey(snapshot.loadedThrough)
      : null;

    if (snapshot?.events?.length && snapshotThrough && snapshotThrough >= firstEnd) {
      featuredRef.current = snapshot.featured || [];
      syncEvents(snapshot.events);
      syncCompleted(new Set(snapshot.completedDayKeys || []));
      const through = parseCalendarDayKey(snapshot.loadedThrough) || firstEnd;
      loadedThroughRef.current = through;
      if (snapshot.aggregate) applyAggregate(snapshot.aggregate);
      setLoading(false);
      setHasMore(hasMoreFromAggregate(snapshot.aggregate?.byDay || {}, through));

      adapter.getAggregate()
        .then((nextAggregate) => {
          if (cancelledRef.current) return null;
          return revalidateFromAggregate(snapshot, nextAggregate, today);
        })
        .catch((err) => {
          if (cancelledRef.current) return;
          reportErrorRef.current(err, {
            pageLabel: 'Events',
            source: 'event-feed-load',
            silent: true,
          });
        });

      return () => {
        cancelledRef.current = true;
      };
    }

    featuredRef.current = [];
    syncEvents([]);
    syncCompleted(new Set());
    loadedThroughRef.current = firstEnd;
    setLoading(true);

    let rangeOk = false;
    let featuredOk = false;

    const rangePromise = adapter.getRange({ start: today, end: firstEnd })
      .then((firstBatch) => {
        if (cancelledRef.current) return;
        rangeOk = true;
        syncEvents(appendUniqueById(eventsRef.current, firstBatch));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        setLoading(false);
        reportFeedLoadError(err);
      });

    const featuredPromise = adapter.getFeatured({ pageSize: FEATURED_EVENTS_PAGE_SIZE })
      .then((featured) => {
        if (cancelledRef.current) return;
        featuredOk = true;
        featuredRef.current = Array.isArray(featured) ? featured : [];
        if (featuredRef.current.length) {
          syncEvents(prependUniqueById(featuredRef.current, eventsRef.current));
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        reportFeedLoadError(err);
      });

    const aggregatePromise = adapter.getAggregate()
      .then((nextAggregate) => {
        if (cancelledRef.current) return;
        applyAggregate(nextAggregate);
      })
      .catch((err) => {
        if (cancelledRef.current) return;
        applyAggregate(aggregateRef.current);
        reportErrorRef.current(err, {
          pageLabel: 'Events',
          source: 'event-feed-load',
          silent: true,
        });
      });

    Promise.all([rangePromise, aggregatePromise]).then(async () => {
      if (cancelledRef.current) return;
      await completeLoadedRange(today, firstEnd);
    });

    Promise.all([rangePromise, featuredPromise, aggregatePromise]).then(async () => {
      if (cancelledRef.current) return;
      await repairFeaturedDays();
      if (rangeOk && featuredOk) persistFeed();
    });

    return () => {
      cancelledRef.current = true;
    };
  }, [
    adapter,
    applyAggregate,
    completeLoadedRange,
    persistFeed,
    repairFeaturedDays,
    reportFeedLoadError,
    revalidateFromAggregate,
    syncCompleted,
    syncEvents,
    initialWindowDays,
  ]);

  const selectedDayKey = selectedDayList(selectedDate, selectedDates)
    .map((day) => calendarDayKey(day))
    .sort()
    .join('|');

  useEffect(() => {
    selectedDayList(selectedDate, selectedDates).forEach((day) => {
      ensureDayLoaded(day);
    });
  }, [selectedDayKey, selectedDate, selectedDates, ensureDayLoaded]);

  const loadNextWindowRef = useRef(loadNextWindow);
  loadNextWindowRef.current = loadNextWindow;

  const attachSentinelObserver = useCallback((node) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    sentinelNodeRef.current = node;
    if (!node || selectedDayKey) return;

    const observer = new IntersectionObserver((entries) => {
      const hit = entries.some((entry) => entry.isIntersecting);
      intersectingRef.current = hit;
      if (!hit) {
        skipFirstIntersectRef.current = false;
        return;
      }
      if (skipFirstIntersectRef.current) {
        skipFirstIntersectRef.current = false;
        return;
      }
      loadNextWindowRef.current();
    }, { rootMargin: SENTINEL_ROOT_MARGIN });
    observer.observe(node);
    observerRef.current = observer;
  }, [selectedDayKey]);

  const sentinelRef = useCallback((node) => {
    attachSentinelObserver(hasMore ? node : null);
  }, [attachSentinelObserver, hasMore]);

  useEffect(() => () => {
    if (observerRef.current) observerRef.current.disconnect();
  }, []);

  const isDayGroupComplete = useCallback((day) => {
    if (!day) return false;
    return completedDays.has(calendarDayKey(day));
  }, [completedDays]);

  return {
    events,
    aggregate,
    countsReady,
    loading,
    loadingMore,
    hasMore,
    completedDays,
    isDayGroupComplete,
    loadNextWindow,
    ensureDayLoaded,
    sentinelRef,
    eventsStillLoading: loading || loadingMore,
  };
}
